import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/AuthService';
import User from '../models/User';

/**
 * Authentication context for the application
 * Provides authentication state and methods for the entire app
 */
const AuthContext = createContext(null);

/**
 * Authentication context provider component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Provider component
 */
export const AuthProvider = ({ children }) => {
  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize authentication state on component mount
  useEffect(() => {
    const initAuth = () => {
      try {
        // Check for existing user session
        if (authService.isAuthenticated()) {
          const user = authService.getCurrentUser();
          setCurrentUser(user);
        }
      } catch (error) {
        console.error("Authentication initialization error:", error);
        setError("认证初始化错误，请刷新页面重试");
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);
  
  /**
   * Login a user
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} Login result
   */
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = authService.login(username, password);
      
      if (result.success) {
        setCurrentUser(result.user);
        return { success: true, user: result.user };
      } else {
        setError(result.message || "登录失败，请检查用户名和密码");
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("登录过程发生错误，请重试");
      return { success: false, message: "登录过程发生错误，请重试" };
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Logout the current user
   * @returns {Promise<Object>} Logout result
   */
  const logout = async () => {
    setLoading(true);
    
    try {
      const result = authService.logout();
      if (result.success) {
        setCurrentUser(null);
      }
      return result;
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, message: "登出过程发生错误" };
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Update user profile
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Update result
   */
  const updateProfile = async (userData) => {
    if (!currentUser) {
      return { success: false, message: "用户未登录" };
    }
    
    setLoading(true);
    
    try {
      const result = authService.updateUser(currentUser.id, userData);
      
      if (result.success) {
        setCurrentUser(result.user);
      }
      
      return result;
    } catch (error) {
      console.error("Profile update error:", error);
      return { success: false, message: "个人资料更新失败" };
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Change result
   */
  const changePassword = async (currentPassword, newPassword) => {
    if (!currentUser) {
      return { success: false, message: "用户未登录" };
    }
    
    setLoading(true);
    
    try {
      return authService.changePassword(currentUser.id, currentPassword, newPassword);
    } catch (error) {
      console.error("Password change error:", error);
      return { success: false, message: "密码更改失败" };
    } finally {
      setLoading(false);
    }
  };

  // Create context value
  const value = {
    currentUser,
    loading,
    error,
    isAuthenticated: !!currentUser,
    isAdmin: currentUser ? authService.isAdmin() : false,
    hasRole: (role) => (currentUser ? authService.hasRole(role) : false),
    hasPermission: (resource, action) => {
      if (!currentUser) return false;
      // Create a User instance to use the proper hasPermission method
      const userInstance = new User(currentUser);
      return userInstance.hasPermission(resource, action);
    },
    login,
    logout,
    updateProfile,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use the auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;